import { personaMeta } from "@narrator/registry";
import { pickPersonaLine } from "@narrator/pickPersonaLine";
import { personaVoice } from "@narrator/personaVoice";
import type { ResultsPersonaId } from "@narrator/personaIds";

/**
 * Soften Slack-oriented pool lines for the web SPA.
 * Keeps dialect; drops host-channel claims (avatars, slash commands, "in Slack").
 */
export function toWebPersonaPlain(line: string): string {
	return line
		.replace(/:[a-z0-9_+-]+:/gi, "")
		.replace(/\*([^*\n]+)\*/g, "$1")
		.replace(/_([^_\n]+)_/g, "$1")
		.replace(/\bin Slack\b/gi, "here")
		.replace(/\bvia Slack\b/gi, "here")
		.replace(/\bReply in this thread\b/gi, "Add options with your curator")
		.replace(/\bTap \*?Vote\*?\b/gi, "Cast your vote")
		.replace(/[ \t]{2,}/g, " ")
		.replace(/\n{3,}/g, "\n\n")
		.trim();
}

function firstBodyParagraph(plain: string, question: string): string {
	const lines = plain
		.split("\n")
		.map((l) => l.trim())
		.filter(Boolean);
	const withoutQuestion = lines.filter((l) => l !== question && !l.startsWith(question));
	const body = (withoutQuestion.length ? withoutQuestion : lines).join(" ");
	return body || plain;
}

/** Status-honest waiting dialect after a ballot is accepted (not “poll still open” reminds). */
const WAITING: Record<ResultsPersonaId, { headline: string; body: string }> = {
	neutral: {
		headline: "Vote recorded — waiting for the curator.",
		body: "You’ll see results when the curator finalizes this poll.",
	},
	analyst: {
		headline: "Ballot logged — awaiting finalize.",
		body: "Tallies publish when the host closes the collection window.",
	},
	executive: {
		headline: "Input received. Decision pending.",
		body: "Results appear when the curator publishes the brief.",
	},
	play_by_play: {
		headline: "Ballot’s in the books — standing by for the call.",
		body: "The reveal drops when the curator wraps the count.",
	},
	pirate: {
		headline: "Yer vote’s aboard — awaitin’ the captain’s seal.",
		body: "The chart posts when the curator finalizes the tally.",
	},
	witty: {
		headline: "Vote parked — hanging for the host wrap-up.",
		body: "Results show when they finalize. No spoilers mid-flight.",
	},
};

export type WebBallotCopy = {
	eyebrow: string;
	intro: string;
	submitLabel: string;
	castingLabel: string;
	waitingHeadline: string;
	waitingBody: string;
	collectingHint: string;
};

export function webBallotCopy(
	personaId: ResultsPersonaId,
	seed: string,
	question: string,
): WebBallotCopy {
	const voice = personaVoice(personaId, seed);
	const introPlain = toWebPersonaPlain(voice.voteModalIntro(question));
	const waiting = WAITING[personaId] ?? WAITING.neutral;
	const collectingRaw = toWebPersonaPlain(
		pickPersonaLine(personaId, "remindCalm", `${seed}:collecting`, { question }),
	);

	return {
		eyebrow: toWebPersonaPlain(voice.voteModalTitle),
		intro: firstBodyParagraph(introPlain, question),
		submitLabel: toWebPersonaPlain(voice.voteSubmit),
		castingLabel: "Sending…",
		waitingHeadline: waiting.headline,
		waitingBody: waiting.body,
		collectingHint: firstBodyParagraph(collectingRaw, question),
	};
}

export type WebCreateCopy = {
	title: string;
	submitLabel: string;
	personaHint: string;
};

export function webCreateCopy(personaId: ResultsPersonaId, seed: string): WebCreateCopy {
	const voice = personaVoice(personaId, seed);
	const meta = personaMeta(personaId);
	return {
		title: toWebPersonaPlain(voice.createModalTitle),
		submitLabel: toWebPersonaPlain(voice.createSubmit),
		personaHint: `Character language for ballots and recaps — ${meta.label}. ${meta.description}. Avatars and channel posts stay Slack-only.`,
	};
}

export type WebCuratorCopy = {
	openVotingLabel: string;
	finalizeLabel: string;
	votingOpenStatus: string;
};

	export function webCuratorCopy(personaId: ResultsPersonaId, seed: string): WebCuratorCopy {
		const voice = personaVoice(personaId, seed);
		return {
			openVotingLabel: "Launch poll — open voting",
			finalizeLabel: toWebPersonaPlain(voice.closeButton),
			votingOpenStatus: "Voting is open",
		};
	}
